/**
 * useViewerCounts Hook
 * Fetches and polls viewer counts for live streams from the PlayerStats API
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE_URL } from '@/client/config';
import type { GetViewerCountsResponse, ViewerCountsByEvent } from '@shared/types';

// Default polling interval: 30 seconds
const DEFAULT_POLL_INTERVAL_MS = 30000;

interface UseViewerCountsOptions {
  /** Polling interval in milliseconds (default: 30000) */
  pollInterval?: number;
  /** Whether to enable polling (default: true) */
  enablePolling?: boolean;
}

interface UseViewerCountsResult {
  /** Viewer counts by event ID */
  viewerCounts: ViewerCountsByEvent;
  /** Total active viewers across all events */
  totalViewers: number;
  /** Whether data is currently being fetched */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether the service is available/configured */
  available: boolean;
  /** Last successful fetch timestamp */
  lastUpdated: Date | null;
  /** Manually trigger a refresh */
  refresh: () => Promise<void>;
}

interface EventsByClient {
  [clientID: string]: string[];
}

/**
 * Group events by client ID for batch requests
 */
function groupEventsByClient(events: Array<{ clientID: string | null; eventID: string | null }>): EventsByClient {
  const grouped: EventsByClient = {};

  for (const event of events) {
    if (!event.clientID || !event.eventID) continue;

    // Extract just the eventID part if it's in "clientID_eventID" format
    const eventIDPart = event.eventID.includes('_')
      ? event.eventID.split('_')[1]
      : event.eventID;

    if (!grouped[event.clientID]) {
      grouped[event.clientID] = [];
    }
    grouped[event.clientID].push(eventIDPart);
  }

  return grouped;
}

/**
 * Hook to fetch and poll viewer counts for live streams
 */
export function useViewerCounts(
  events: Array<{ clientID: string | null; eventID: string | null }>,
  options: UseViewerCountsOptions = {}
): UseViewerCountsResult {
  const { pollInterval = DEFAULT_POLL_INTERVAL_MS, enablePolling = true } = options;
  const { getAccessTokenSilently, logout } = useAuth0();

  const [viewerCounts, setViewerCounts] = useState<ViewerCountsByEvent>({});
  const [totalViewers, setTotalViewers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Track if component is mounted
  const mountedRef = useRef(true);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Fetch viewer counts for a single client
   */
  const fetchForClient = useCallback(async (
    clientID: string,
    eventIDs: string[],
    token: string
  ): Promise<GetViewerCountsResponse | null> => {
    try {
      const params = new URLSearchParams({
        clientID,
        eventIds: eventIDs.join(','),
      });

      const response = await fetch(`${API_BASE_URL}/api/analytics/viewers?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 503) {
        // Service unavailable - not configured
        return null;
      }

      if (response.status === 401) {
        logout({ logoutParams: { returnTo: window.location.origin } });
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error(`Failed to fetch viewer counts for client ${clientID}:`, err);
      return null;
    }
  }, [logout]);

  /**
   * Fetch all viewer counts (for all clients with live events)
   */
  const fetchViewerCounts = useCallback(async () => {
    // Skip if no events to fetch
    const eventsByClient = groupEventsByClient(events);
    const clientIDs = Object.keys(eventsByClient);

    if (clientIDs.length === 0) {
      setViewerCounts({});
      setTotalViewers(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      // Fetch viewer counts for each client in parallel
      const results = await Promise.all(
        clientIDs.map(clientID =>
          fetchForClient(clientID, eventsByClient[clientID], token)
        )
      );

      if (!mountedRef.current) return;

      // Check if service is available
      const allUnavailable = results.every(r => r === null);
      if (allUnavailable && results.length > 0) {
        setAvailable(false);
        setLoading(false);
        return;
      }

      // Merge all results
      const mergedCounts: ViewerCountsByEvent = {};
      let total = 0;

      for (const result of results) {
        if (!result) continue;
        Object.assign(mergedCounts, result.byEvent);
        total += result.total;
      }

      setViewerCounts(mergedCounts);
      setTotalViewers(total);
      setAvailable(true);
      setLastUpdated(new Date());
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      console.error('Failed to fetch viewer counts:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [events, getAccessTokenSilently, fetchForClient]);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(async () => {
    await fetchViewerCounts();
  }, [fetchViewerCounts]);

  // Initial fetch and polling setup
  useEffect(() => {
    mountedRef.current = true;

    // Initial fetch
    fetchViewerCounts();

    // Setup polling
    if (enablePolling && pollInterval > 0) {
      const poll = () => {
        pollTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            fetchViewerCounts().then(() => {
              if (mountedRef.current) {
                poll();
              }
            });
          }
        }, pollInterval);
      };
      poll();
    }

    return () => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [fetchViewerCounts, enablePolling, pollInterval]);

  return {
    viewerCounts,
    totalViewers,
    loading,
    error,
    available,
    lastUpdated,
    refresh,
  };
}
